import DeliveryAddressSelect from '@components/dealer-table/DeliveryAddressSelect';
import InfoTooltip from '@components/InfoTooltip';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  makeStyles,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  AddCircleOutlineOutlined,
  CheckCircleOutline as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorIcon,
  Save as SaveIcon,
  Smartphone as SmartphoneIcon,
} from '@material-ui/icons';
import { useIsMutating } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationKeys } from 'src/constants';
import { mfaLoginErrorKey, useMfaLogin, useMfaLoginResult } from 'src/hooks/mfa-login';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import { formatDate } from 'src/modules/date-helpers';

import {
  DeliveryAddressResponse,
  MultiFactorLoginTypeBasic,
  PlatformCredentialsDTO,
  PlatformCredentialsMetadataDTO,
  SourceType,
  UsernameStatusBasic,
} from '../../modules/generated/api';
import { formatDeliveryAddress, getLabel, sources, USERNAME_STATUS_LABELS } from '../../modules/labels';
import theme from '../../setup/theme';

import { FirstCallRulesDrawer } from './FirstCallRulesDrawer';
import { useCompactActions } from './useCompactActions';

const useStyles = makeStyles({
  actions: {
    width: 220,
    whiteSpace: 'nowrap',
    '& > * + *': {
      marginInlineStart: theme.spacing(2),
    },
    '& > $mfaResult': {
      marginInlineStart: 0,
    },
  },
  mfaResult: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBlockStart: theme.spacing(0.5),
    textAlign: 'left',
    whiteSpace: 'normal',
  },
  mfaResultIcon: {
    fontSize: '1rem',
    marginInlineEnd: theme.spacing(0.5),
    marginBlockStart: 2,
  },
  mfaResultSuccess: {
    color: theme.palette.success.main,
  },
  mfaResultError: {
    color: theme.palette.error.main,
  },
  mfaResultText: {
    textWrap: 'balance',
  },

  spinner: {
    display: 'inline-block',
    width: '1em !important',
    height: '1em !important',
    marginInlineStart: theme.spacing(1),
  },
  tooltipTrigger: {
    textDecoration: 'underline dotted',
    cursor: 'help',
  },
  usernameCompact: {
    display: 'inline-block',
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  statusCell: {
    paddingRight: 0,
  },
  field: {
    minWidth: 140,
  },
  mfaAction: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  kvps: {
    padding: 0,
    paddingLeft: '1em',
    margin: 0,
  },
  detailHeadline: {
    fontWeight: theme.typography.fontWeightBold as number,
    marginBlockEnd: theme.spacing(1),
  },
  iconsAlign: {
    display: 'flex',
    alignItems: 'center',
  },
  noMargin: {
    margin: 0,
  },
});

const getStatusColor = (status: UsernameStatusBasic): string =>
  ({
    [UsernameStatusBasic.Disabled]: theme.palette.error.main,
    [UsernameStatusBasic.Enabled]: theme.palette.success.main,
    [UsernameStatusBasic.Pending]: theme.palette.grey[300],
  })[status];

type DealerInfoProps = {
  kvps: string[];
  kvpsDisplayNames?: Record<string, string>;
};

const DealerInfos = ({ kvps, kvpsDisplayNames }: DealerInfoProps): React.ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div>
      <Typography variant="body2" className={classes.detailHeadline}>
        {t('dealerTable.infosHeadline')}
      </Typography>
      <ul className={classes.kvps}>
        {kvps.map((id) => (
          <li key={id}>
            {/* PPN credentials use Porsche center IDs (UUIDs) as KVPS, so show their names instead */}
            <Typography variant="body2">{kvpsDisplayNames?.[id] ?? id}</Typography>
          </li>
        ))}
      </ul>
    </div>
  );
};

type DealerRowProps = {
  username: string;
  metadata: PlatformCredentialsMetadataDTO;
  onDelete: (id: string) => void;
  onSave: (id: string, username: string, password: string, metadata: PlatformCredentialsMetadataDTO) => void;
  validated?: boolean;
  status: UsernameStatusBasic;
  kvps: string[];
  kvpsDisplayNames?: Record<string, string>;
  displayPostalCode: boolean;
  source: SourceType;
  id: string;
  deliveryAddresses?: DeliveryAddressResponse[];
  classes: { select: string };
  // Address that should be displayed when not edited
  deliveryAddress?: DeliveryAddressResponse;
  // Whether this credential requires an MFA login (IRIS-6572) — drives the PPN login action
  mfaLoginType?: MultiFactorLoginTypeBasic;
  onMfaLoginSuccess?: () => void;
};

const DealerRow = ({
  username,
  metadata,
  onDelete,
  onSave,
  validated,
  status,
  kvps,
  kvpsDisplayNames,
  displayPostalCode,
  source,
  id,
  deliveryAddresses,
  deliveryAddress,
  mfaLoginType,
  onMfaLoginSuccess,
  classes: externalClasses,
}: DealerRowProps): React.ReactElement => {
  const { t } = useTranslation();
  const snackbar = useCustomSnackbarGlobal();
  const classes = { ...useStyles(), ...externalClasses };
  const [editMode, setEditMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loading = validated === false;
  const renderInfos = kvps.length > 0;
  const form = useForm<PlatformCredentialsDTO>({
    defaultValues: {
      metadata,
    },
  });
  const { control, handleSubmit, setValue } = form;
  const selectedSourceOption = sources.find((s) => s.value === source);
  const rules = (metadata.automaticBiddingTimeExtension?.rules || []).map((rule) => rule.postalCodePattern);

  const handleClickEdit = () => {
    setEditMode(true);
  };

  const handleClickCancel = () => {
    setValue('password', '');
    setEditMode(false);
  };

  const mfaLoginResult = useMfaLoginResult(id);
  const mfaLogin = useMfaLogin(id, {
    onSuccess: () => {
      snackbar.showSuccess(t('dealerManagementPage.mfaLogin.success'));
      onMfaLoginSuccess?.();
    },
    // The persisted result carries the same message, but the snackbar is what reaches a user who
    // has since scrolled or navigated away.
    onError: ({ httpStatus }) => snackbar.showError(t(mfaLoginErrorKey(httpStatus))),
  });
  const compactActions = useCompactActions();
  const supportsMfaLogin = mfaLoginType === MultiFactorLoginTypeBasic.Authenticator;
  // Read from the mutation cache rather than the local mutation: the request outlives this row, so
  // a login started before navigating away is still shown as running when the user comes back.
  const mfaLoginPending = useIsMutating({ mutationKey: [MutationKeys.mfaLogin, id] }) > 0;
  // Only needed once the label is gone. Empty while saving, because MUI cannot bind a tooltip to a
  // disabled button.
  const editTooltip = compactActions && !loading ? t('common.edit') : '';
  // Only worth a tooltip when it carries something the cell is not already showing: the full
  // username while it is ellipsised, and the KVPS list when there is one.
  const usernameTooltip =
    compactActions || renderInfos ? (
      <>
        {compactActions && <Typography variant="caption">{username}</Typography>}
        {renderInfos && <DealerInfos kvps={kvps} kvpsDisplayNames={kvpsDisplayNames} />}
      </>
    ) : (
      ''
    );

  const mfaLoginFailed = mfaLoginResult?.status === 'error';
  const mfaLoginLabelKey = () => {
    if (mfaLoginPending) return 'dealerManagementPage.mfaLogin.pending';
    if (compactActions) return 'dealerManagementPage.mfaLogin.actionShort';

    return mfaLoginFailed ? 'dealerManagementPage.mfaLogin.retry' : 'dealerManagementPage.mfaLogin.action';
  };

  const handleClickMfaLogin = () => {
    snackbar.showInfo(t('dealerManagementPage.mfaLogin.pendingHint'));
    mfaLogin.mutate({ username, source });
  };

  const handleClickSave = handleSubmit(({ password, metadata: newMetdata }) => {
    onSave(id, username, password ?? '', newMetdata!);
    setValue('password', '');
    setEditMode(false);
  });

  return (
    <FormProvider {...form}>
      <TableRow>
        <TableCell component="th" scope="row">
          <Typography variant="body2" component="div">
            <Tooltip title={usernameTooltip} placement="bottom-start">
              <span className={clsx(compactActions && classes.usernameCompact, renderInfos && classes.tooltipTrigger)}>
                {username}
              </span>
            </Tooltip>
            {loading && <CircularProgress className={classes.spinner} />}
          </Typography>
        </TableCell>
        <TableCell>
          {editMode ? (
            <Controller
              control={control}
              name="metadata.displayName"
              render={({ field }) => (
                <TextField
                  className={classes.field}
                  label={t('contact.displayName')}
                  type="text"
                  size="small"
                  variant="outlined"
                  {...field}
                />
              )}
            />
          ) : (
            <TextField
              className={classes.field}
              disabled
              size="small"
              variant="outlined"
              value={metadata?.displayName}
            />
          )}
        </TableCell>
        <TableCell>
          {editMode ? (
            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <TextField
                  className={classes.field}
                  label={t('common.password')}
                  type="text"
                  size="small"
                  variant="outlined"
                  {...field}
                />
              )}
            />
          ) : (
            <TextField
              className={classes.field}
              type="password"
              disabled
              size="small"
              variant="outlined"
              value="password-hidden"
            />
          )}
        </TableCell>
        {displayPostalCode && (
          <TableCell>
            {editMode ? (
              <Controller
                control={control}
                name="metadata.postalCode"
                render={({ field }) => (
                  <TextField
                    className={classes.field}
                    label={t('contact.postal')}
                    type="text"
                    size="small"
                    variant="outlined"
                    {...field}
                  />
                )}
              />
            ) : (
              <TextField
                className={classes.field}
                disabled
                size="small"
                variant="outlined"
                value={metadata?.postalCode || t('contact.postal')}
              />
            )}
          </TableCell>
        )}

        <TableCell>{selectedSourceOption ? t(selectedSourceOption.label as any) : 'UNKNOWN'}</TableCell>

        <TableCell>
          {editMode && (
            <DeliveryAddressSelect
              name="metadata.defaultDeliveryAddressId"
              deliveryAddresses={deliveryAddresses}
              control={control}
              className={classes.select}
            />
          )}
          {!editMode && deliveryAddress && formatDeliveryAddress(deliveryAddress)}
          {!editMode && !deliveryAddress && deliveryAddresses !== undefined && 'N/A'}
        </TableCell>

        <TableCell>
          {editMode ? (
            <Controller
              control={control}
              name="metadata.automaticBiddingTimeExtension.enabled"
              render={({ field }) => (
                <div className={classes.iconsAlign}>
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label=""
                    className={classes.noMargin}
                  />
                  <IconButton size="small" color="secondary" aria-label="help" onClick={() => setDrawerOpen(true)}>
                    <AddCircleOutlineOutlined fontSize="small" />
                  </IconButton>
                </div>
              )}
            />
          ) : (
            <div>
              <Checkbox
                style={{ margin: 0 }}
                disabled
                checked={metadata?.automaticBiddingTimeExtension?.enabled}
                inputProps={{ 'aria-label': t('dealerManagementPage.automaticFirstCallExtension') }}
              />
              <InfoTooltip
                color={rules.length === 0 && metadata?.automaticBiddingTimeExtension?.enabled ? 'primary' : 'inherit'}
                title={
                  rules.length === 0
                    ? t('dealerManagementPage.noRules')
                    : t('dealerManagementPage.definedRules', {
                        count: metadata.automaticBiddingTimeExtension?.rules?.length || 0,
                      })
                }
                hint={rules.join(', ')}
              />
            </div>
          )}
        </TableCell>

        <TableCell className={classes.statusCell}>
          <Chip
            label={getLabel(USERNAME_STATUS_LABELS, status)}
            size="small"
            style={{ background: getStatusColor(status) }}
          />
        </TableCell>

        <TableCell align="right" className={classes.actions}>
          {!editMode && supportsMfaLogin && (
            <span className={classes.mfaAction}>
              <InfoTooltip placement="top" hint={t('dealerManagementPage.mfaLogin.info')} />
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleClickMfaLogin}
                disabled={mfaLoginPending || loading}
                startIcon={mfaLoginPending ? <CircularProgress size={16} /> : <SmartphoneIcon />}
              >
                {t(mfaLoginLabelKey())}
              </Button>
            </span>
          )}
          {editMode ? (
            <Button variant="text" color="secondary" onClick={handleClickCancel}>
              {t('common.cancel')}
            </Button>
          ) : (
            <IconButton
              size="small"
              color="secondary"
              aria-label={t('common.delete')}
              onClick={() => onDelete(id)}
              disabled={loading}
            >
              <DeleteIcon />
            </IconButton>
          )}
          {editMode ? (
            <Button variant="contained" startIcon={<SaveIcon />} color="primary" onClick={handleClickSave} size="small">
              {t('common.save')}
            </Button>
          ) : (
            <Tooltip title={editTooltip}>
              <Button
                variant="contained"
                startIcon={compactActions ? undefined : <EditIcon />}
                color="secondary"
                onClick={handleClickEdit}
                disabled={loading}
                size="small"
                aria-label={t('common.edit')}
              >
                {compactActions ? <EditIcon fontSize="small" /> : t('common.edit')}
              </Button>
            </Tooltip>
          )}
          {!editMode && supportsMfaLogin && mfaLoginResult && (
            <div className={classes.mfaResult}>
              {mfaLoginResult.status === 'success' ? (
                <CheckCircleIcon className={clsx(classes.mfaResultIcon, classes.mfaResultSuccess)} />
              ) : (
                <ErrorIcon className={clsx(classes.mfaResultIcon, classes.mfaResultError)} />
              )}
              <Typography variant="caption" component="span" className={classes.mfaResultText}>
                {mfaLoginResult.status === 'success'
                  ? t('dealerManagementPage.mfaLogin.lastSuccess', { time: formatDate(mfaLoginResult.finishedAt) })
                  : t('dealerManagementPage.mfaLogin.errorAt', {
                      message: t(mfaLoginErrorKey(mfaLoginResult.httpStatus)),
                      time: formatDate(mfaLoginResult.finishedAt),
                    })}
              </Typography>
            </div>
          )}
        </TableCell>
      </TableRow>
      {!!drawerOpen && <FirstCallRulesDrawer open={!!drawerOpen} onClose={() => setDrawerOpen(false)} />}
    </FormProvider>
  );
};

export default DealerRow;
